import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

// Minimal typings for the parts of the IFrame API we use.
interface YTPlayer {
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  destroy(): void;
}

declare global {
  interface Window {
    YT?: { Player: new (el: HTMLElement, opts: unknown) => YTPlayer; PlayerState: { PLAYING: number } };
    onYouTubeIframeAPIReady?: () => void;
  }
}

// The IFrame API script loads once per page; every player awaits the same promise.
let apiPromise: Promise<void> | null = null;
function loadIframeApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  if (!apiPromise) {
    apiPromise = new Promise<void>((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        prev?.();
        resolve();
      };
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(script);
    });
  }
  return apiPromise;
}

export interface VideoPlayerHandle {
  seekTo(seconds: number): void;
}

interface Props {
  videoId: string;
  /** Resume point applied on load. */
  startSeconds?: number;
  /** ~1s ticks while the video is playing. */
  onTime?: (seconds: number) => void;
  /** Fired on pause and ended — the moment to persist position. */
  onPause?: (seconds: number) => void;
}

/**
 * YouTube IFrame Player in a responsive 16:9 frame. Polls getCurrentTime()
 * once a second, but only while playing — paused videos cost nothing.
 */
export const VideoPlayer = forwardRef<VideoPlayerHandle, Props>(function VideoPlayer(
  { videoId, startSeconds, onTime, onPause },
  ref,
) {
  const mountRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  // Callbacks live in refs so the player effect only re-runs on a new video.
  const onTimeRef = useRef(onTime);
  const onPauseRef = useRef(onPause);
  useEffect(() => {
    onTimeRef.current = onTime;
    onPauseRef.current = onPause;
  }, [onTime, onPause]);

  useImperativeHandle(ref, () => ({
    seekTo: (seconds: number) => playerRef.current?.seekTo(seconds, true),
  }));

  useEffect(() => {
    let cancelled = false;
    const stopPolling = () => {
      clearInterval(pollRef.current);
      pollRef.current = undefined;
    };

    loadIframeApi().then(() => {
      if (cancelled || !mountRef.current || !window.YT) return;
      playerRef.current = new window.YT.Player(mountRef.current, {
        videoId,
        playerVars: { start: Math.floor(startSeconds ?? 0), rel: 0 },
        events: {
          onStateChange: (e: { data: number }) => {
            const player = playerRef.current;
            if (!player || !window.YT) return;
            if (e.data === window.YT.PlayerState.PLAYING) {
              stopPolling();
              pollRef.current = setInterval(() => {
                onTimeRef.current?.(player.getCurrentTime());
              }, 1000);
            } else {
              stopPolling();
              onPauseRef.current?.(player.getCurrentTime());
            }
          },
        },
      });
    });

    return () => {
      cancelled = true;
      stopPolling();
      playerRef.current?.destroy();
      playerRef.current = null;
    };
    // startSeconds is a load-time resume point, not a live binding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  return (
    <div className="relative w-full rounded-xl overflow-hidden" style={{ aspectRatio: '16 / 9', background: '#0E0E18', border: '1px solid #1E1E34' }}>
      {/* YT.Player replaces this div with the iframe */}
      <div ref={mountRef} className="absolute inset-0 w-full h-full" />
    </div>
  );
});
